import { updateNotificationConfig } from "services/companyAPI";
import { getNotificationPreferences, updateNotificationPreferences } from "services/userService";
import { store } from "reducers/store";

const pending = new Map();
let generation = 0;
const isCurrent = (id, version) => generation === version && store.getState().userReducer?.user?.id === id;
const checkAccount = (id, version) => {
  if (!isCurrent(id, version)) throw new Error("Contul s-a schimbat. Redeschide notificările pentru contul curent.");
};
export const supportsPush = () => "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;

function withTimeout(promise, ms = 10000) {
  let timer;
  return Promise.race([promise, new Promise((_, reject) => {
    timer = setTimeout(() => reject(new Error("Notificările nu sunt disponibile momentan. Reîncearcă după redeschiderea aplicației.")), ms);
  })]).finally(() => clearTimeout(timer));
}

export async function registerPushNotification(user, { requestPermission = false } = {}) {
  const version = generation;
  if (!user?.id || !supportsPush()) return { enabled: false, reason: "unsupported" };
  // Request directly from the button's gesture, before waiting for anything else.
  const permission = Notification.permission === "default" && requestPermission
    ? await Notification.requestPermission() : Notification.permission;
  if (permission !== "granted") return { enabled: false, reason: permission };
  if (!process.env.REACT_APP_PUSH_KEY) throw new Error("Notificările push nu sunt configurate momentan.");
  if (pending.has(user.id)) return pending.get(user.id);
  const registration = (async () => {
    const worker = await withTimeout(navigator.serviceWorker.ready);
    checkAccount(user.id, version);
    const subscription = await worker.pushManager.getSubscription() || await worker.pushManager.subscribe({
      userVisibleOnly: true, applicationServerKey: urlBase64ToUint8Array(process.env.REACT_APP_PUSH_KEY),
    });
    if (!isCurrent(user.id, version)) {
      await subscription.unsubscribe();
      checkAccount(user.id, version);
    }
    await updateNotificationConfig("push", { ...subscription.toJSON(), encoding: window.PushManager.supportedContentEncodings });
    checkAccount(user.id, version);
    return { enabled: true };
  })();
  pending.set(user.id, registration);
  try { return await registration; }
  finally { if (pending.get(user.id) === registration) pending.delete(user.id); }
}

export async function enableDocumentPushNotifications(user) {
  const version = generation;
  const result = await registerPushNotification(user, { requestPermission: true });
  if (!result.enabled) return result;
  checkAccount(user.id, version);
  const response = await getNotificationPreferences();
  checkAccount(user.id, version);
  const preferences = response.data || {};
  await updateNotificationPreferences({ ...preferences,
    DOCUMENT_REQUESTED: { ...preferences.DOCUMENT_REQUESTED, push: true },
    DOCUMENT_COMMENTED: { ...preferences.DOCUMENT_COMMENTED, push: true },
  });
  return result;
}

export async function unregisterPushNotification() {
  generation++;
  pending.clear();
  if (!supportsPush()) return;
  const worker = await navigator.serviceWorker.getRegistration();
  const subscription = await worker?.pushManager.getSubscription();
  if (subscription) await subscription.unsubscribe();
}

function urlBase64ToUint8Array(value) {
  const padding = "=".repeat((4 - value.length % 4) % 4);
  const data = window.atob((value + padding).replace(/-/g, "+").replace(/_/g, "/"));
  return Uint8Array.from(data, (character) => character.charCodeAt(0));
}
